import katex from 'katex';
import { sanitizeHtml } from '../../utils/sanitize';
import { espacerMilliersHtml, espacerMilliersTex } from '../../utils/milliers';
import { RUN_RE, SEUIL_LONGUE, decouperTexte, decouperTex, texVisible } from '../Print/helpers/expressions';

/**
 * Rend une chaîne HTML dont les formules `$…$` deviennent du KaTeX, en UNE
 * seule chaîne HTML prête pour dangerouslySetInnerHTML.
 *
 * Pourquoi pas un découpage en morceaux (texte / formule / texte) rendus
 * séparément : chaque morceau était nettoyé à part, et le nettoyeur refermait
 * le `<p>` ouvert avant la formule. « Un côté de $\dfrac{13}{4}$ cm. » se
 * rendait comme deux blocs, la fraction et la suite partaient à la ligne
 * (constat utilisateur 2026-09-06). Ici :
 *   1. les formules sont mises de côté, remplacées par un jeton texte ;
 *   2. le HTML entier est nettoyé d'un bloc (les balises restent appariées)
 *      et ses milliers espacés ;
 *   3. chaque jeton est remplacé par la formule rendue par KaTeX, produite
 *      localement, donc après le nettoyage.
 *
 * @param {string} html
 * @param {RegExp} regex - repérage des formules ; STRICTE par défaut (le `$`
 *   ouvrant doit être suivi d'un `\`, cf. RichTextBlockEleve / HtmlKatex)
 * @returns {string}
 */
export const KATEX_STRICT_RE = /\$(\\[^$]+)\$/g;

const JETON = '⁣';
const BALISE_RE = /(<\/?[a-zA-Z][^>]*>)/;

/**
 * Expressions JAMAIS coupées à l'écran (2026-09-12, la règle du papier
 * transposée, cf. Print/helpers/renderKatex.js) : une expression en texte
 * (« 48 − 8 × 10 ÷ 4 = ? ») comme une formule KaTeX est enveloppée dans
 * .expr-insecable (white-space: nowrap). Si elle ne tient pas sur la ligne en
 * cours, elle descend ENTIÈRE à la suivante ; si elle tient, elle y reste :
 * une fraction seule au milieu d'une phrase n'a pas de ligne à elle. Au-delà
 * de SEUIL_LONGUE caractères visibles, bloc de lignes coupé au « = »
 * (.expr-bloc / .expr-ligne), à défaut après + / −. Avant, l'énoncé projeté
 * se repliait à n'importe quel espace, et KaTeX autorisait un saut devant
 * un « = » (constat prof, mode classe QR code).
 */
const bloc = (lignesHtml, indentCh) => `<span class="expr-bloc">${
    lignesHtml.map((l, i) => `<span class="expr-ligne"${
        i > 0 && indentCh ? ` style="padding-left:${indentCh}ch"` : ''
    }>${l}</span>`).join('')
}</span>`;

const envelopperRunTexte = (run) => {
    if (run.length <= SEUIL_LONGUE) return `<span class="expr-insecable">${run}</span>`;
    const { lignes, indent } = decouperTexte(run);
    return bloc(lignes, indent);
};

const katexInline = (tex) => katex.renderToString(tex, { throwOnError: false, displayMode: false });

const rendreFormule = (tex) => {
    if (texVisible(tex) <= SEUIL_LONGUE) return `<span class="expr-insecable">${katexInline(tex)}</span>`;
    const { lignes, indent } = decouperTex(tex);
    return bloc(lignes.map(katexInline), indent);
};

/** Les expressions en texte, hors balises, deviennent insécables. Les jetons
 *  de formules sont du texte à ce stade : une formule prise dans un calcul
 *  (« ⁣K0⁣ = 5 ») reste dans le même bloc insécable que lui. */
const envelopperExpressionsTexte = (html) => html.split(BALISE_RE).map((morceau, i) => (
    i % 2 === 1 ? morceau : morceau.replace(RUN_RE, envelopperRunTexte)
)).join('');

export const htmlAvecKatex = (html, regex = KATEX_STRICT_RE) => {
    if (typeof html !== 'string' || !html) return '';
    const formules = [];
    const avecJetons = html.replace(new RegExp(regex.source, 'g'), (m, tex) => {
        formules.push(tex);
        return `${JETON}K${formules.length - 1}${JETON}`;
    });
    const propre = envelopperExpressionsTexte(espacerMilliersHtml(sanitizeHtml(avecJetons)));
    if (formules.length === 0) return propre;
    return propre.replace(new RegExp(`${JETON}K(\\d+)${JETON}`, 'g'), (m, i) => {
        const tex = formules[Number(i)];
        try {
            return rendreFormule(espacerMilliersTex(tex));
        } catch {
            return `$${tex}$`;
        }
    });
};
